/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * SPUserCore — server-safe SharePoint user data classes.
 *
 * Exports:
 *   SPUserCore      — single user object with pure data/logic (no browser APIs)
 *   SPUsersCore     — typed collection of SPUserCore instances
 *
 * Browser-dependent image handling (SPUserImage) stays in the browser-only
 * ReUsableTS/SPUsers.ts module.
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 */

import {
    getCoreUtils, kNotNullOrUndefined, kNotEmptyString, nz, doCallBack, kCore,
} from "./kCoreHandler";
import { kObjectCore, kObjectArrayCore } from "./kCoreObjects";
import { kSPRestCore } from "./kSPRestCore";
import { kSettleOnce } from "./kSettleOnce";

// ── Pure helper ──────────────────────────────────────────────────────────────

/**
 * Extract up to `InitialCount` initials from a display name (first letter of the first N space-split
 * parts, uppercased; falls back to the first character). Initials are USER-identity logic, so they live
 * with the user model — EXPORTED so offline consumers (e.g. the MOSE image→PDF avatar) reuse it instead
 * of re-implementing. Importing this module is safe now that kSPRestCore constructs its RealUsers cache
 * lazily (no eager `new SPUsersCore()` at load), so the old circular-import trap is gone. Env-neutral
 * (no browser API); equivalent to the browser `ks.GetInitials`.
 */
export const getInitials = (Name: string, InitialCount: number = 2): string => {
    if (getCoreUtils().KErrorMode) { return ""; }
    try {
        return kCore.GetInitials(Name, InitialCount);
    } catch (e) {
        getCoreUtils().KGlobalErrorHandler(e, "SPUserCore.getInitials");
        return "";
    }
};

// ── SPUserCore ───────────────────────────────────────────────────────────────

export class SPUserCore extends kObjectCore {

    public Pic: any = null;
    public IDSecond: number = 0;
    public Login: string = "";
    // EMail is mirrored from Login in FromRestObj when no real EMail
    // is present on the REST response. Modern M365 SiteUserInfoList
    // returns EMail = "" for most rows due to the M365 attribute
    // restriction; Login already carries the bare UPN
    // (i:0#.f|membership| claims prefix is stripped below), and
    // userphoto.aspx / mailto links / display fallbacks all work fine
    // with the UPN, so callers that read EMail get a usable address
    // without having to know about the Login fallback.
    public EMail: string = "";
    public ExistsInSiteCollection: boolean = false;
    public ExistsInCurrentWeb: boolean = false;
    public ExamplePerson: boolean = false;
    public Initials: string = "";

    private _EnsureInSiteCollectionCB: any = null;

    constructor(Title?: string) {
        super(Title);
    }

    // ── Public methods ───────────────────────────────────────────────────────

    public SetName = (NewName: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.Title = NewName;
            this.SetInitials();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore.SetName");
        }
    };

    public FromSPUserInfo = (UserInfoObject: any): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (UserInfoObject.ID > 0) {
                this.Login = this.LoginFromUserName(UserInfoObject.Name);
                this.ID = UserInfoObject.ID;
                this.Title = UserInfoObject.Title;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore.FromSPUserInfo");
        }
    };

    public LoginFromUserName = (UserName: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            // PX-147: a missing user name is a caller bug - report it through the handler (the catch below).
            if (UserName === null || UserName === undefined) { throw new Error("SPUserCore.LoginFromUserName: no user name (" + String(UserName) + ")"); }
            return SPUserCore.LoginOf(UserName);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore.LoginFromUserName");
            return "";
        }
    };

    public SetInitials = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (this.Initials === "") {
                this.Initials = getInitials(this.Title);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore.SetInitials");
        }
    };

    /**
     * Populate this user from a SharePoint REST response object.
     *
     * The CheckPic parameter is accepted for API compatibility with the
     * browser version but image checking is skipped — SPUserImage is
     * browser-only.
     */
    public FromRestObj = (RestObj: any, CheckPic?: boolean): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            // The three steps below are ORDER-DEPENDENT and must stay in this order:
            // _ApplyLoginAndEmail strips the claims prefix that _ApplyPicture never sees
            // but that the EMail mirror depends on. CheckPic is accepted for browser API
            // compatibility and intentionally unused here - see the doc comment.
            this.SetName(RestObj.Title);
            this._ApplyIds(RestObj);
            this._ApplyLoginAndEmail(RestObj);
            this._ApplyPicture(RestObj);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore.FromRestObj");
        }
    };

    /**
     * ID / IDSecond from a REST row. Sometimes a user has two IDs, and SharePoint
     * spells the key both "ID" and "Id" depending on the endpoint - so both spellings
     * are read, and the SECOND id seen lands on IDSecond rather than overwriting.
     */
    private _ApplyIds = (RestObj: any): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let Found: number = 0;
            let Has: boolean = false;
            if (kNotNullOrUndefined(RestObj.ID)) {
                Found = RestObj.ID;
                Has = true;
            } else {
                if (kNotNullOrUndefined(RestObj.Id)) {
                    Found = RestObj.Id;
                    Has = true;
                }
            }
            if (Has === false) { return; }
            if (this.ID === 0) {
                this.ID = Found;
                return;
            }
            this.IDSecond = Found;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore._ApplyIds");
        }
    };

    /**
     * Login + EMail from a REST row, claims prefix stripped and lower-cased.
     *
     * The final mirror is deliberate: modern M365 tenants return EMail = "" for most
     * SiteUserInfoList rows while Login carries the bare UPN once the claims prefix is
     * gone, so consumers reading EMail would otherwise get nothing. It only fires when
     * EMail is still empty AND Login looks like an address, so a raw system-account or
     * SharePoint App login name is never mirrored into an email field.
     */
    private _ApplyLoginAndEmail = (RestObj: any): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (kNotEmptyString(RestObj.EMail)) {
                this.Login = RestObj.EMail;
                this.EMail = RestObj.EMail;
            } else {
                if (kNotEmptyString(RestObj.Email)) {
                    this.Login = RestObj.Email;
                    this.EMail = RestObj.Email;
                } else {
                    this._ApplyLoginFromName(RestObj);
                }
            }
            if (kNotNullOrUndefined(this.Login)) {
                this.Login = this.Login.toLowerCase();
            }
            if (this.EMail === ""
                && kNotEmptyString(this.Login)
                && this.Login.indexOf("@") !== -1) {
                this.EMail = this.Login;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore._ApplyLoginAndEmail");
        }
    };

    /** Login from LoginName / Name, with any `i:0#.w|DOMAIN\user` claims prefix removed. */
    private _ApplyLoginFromName = (RestObj: any): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (kNotNullOrUndefined(RestObj.LoginName)) {
                this.Login = RestObj.LoginName;
            } else {
                this.Login = RestObj.Name;
            }
            if (!kNotNullOrUndefined(this.Login)) { return; }
            this.Login = SPUserCore.LoginOf(this.Login);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore._ApplyLoginFromName");
        }
    };

    /**
     * Picture path, stored on Pic when the browser subclass has assigned an
     * SPUserImage instance. The core has no image type of its own, so an absent Pic is
     * the normal node-side state and not a failure.
     */
    private _ApplyPicture = (RestObj: any): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (!kNotNullOrUndefined(RestObj.Picture)) { return; }
            if (!kNotNullOrUndefined(RestObj.Picture.Url)) { return; }
            if (!kNotNullOrUndefined(this.Pic)) { return; }
            this.Pic.PicPath = RestObj.Picture.Url;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore._ApplyPicture");
        }
    };

    public EnsureInSiteCollection = (CallBack: any): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this._EnsureInSiteCollectionCB = CallBack;
            const Body: any = {
                "__metadata": { "type": "SP.User" },
                "LoginName": this.Login,
            };
            kSPRestCore.RestExecute(
                "POST",
                "/_api/web/siteusers",
                this._UserAddedToSiteCollection,
                Body,
                "Ensuring user in site collection for user with login " + this.Login,
            );
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore.EnsureInSiteCollection");
        }
    };

    /**
     * Look up a user's FIRST name from the SharePoint User Profile service
     * (SP.UserProfiles.PeopleManager.GetUserProfilePropertyFor) — NO Graph, so it
     * works on classic .wsp pages and app-only server contexts. Calls back with
     * the FirstName, or "" when the profile has none / cannot be read (an expected
     * condition — the caller falls back to the display name). `Account` may be a
     * claims login ("i:0#.f|membership|user@x") or a bare UPN / e-mail (wrapped as
     * the standard membership claim).
     */
    public GetFirstName = (Account: string, CallBack: (FirstName: string) => void): void => {
        // One request object per call (2026-10-09 H5): the callback used to sit in one instance slot, so a second call before the first answered
        // overwrote it - the first caller never heard and the second got the first request's answer. The op answers through kSettleOnce.
        const Op: SPUserFirstNameOp = new SPUserFirstNameOp(CallBack);
        // Gate already up: answer "" instead of stranding the caller (2026-10-09, G16).
        if (getCoreUtils().KErrorMode) { Op.Deliver(""); return; }
        try {
            const Claims: string = SPUserCore._ClaimsAccount(Account);
            if (!kNotEmptyString(Claims)) { Op.Deliver(""); return; }
            const Url: string = "/_api/SP.UserProfiles.PeopleManager/"
                + "GetUserProfilePropertyFor(accountName=@v,propertyName='FirstName')?@v='"
                + encodeURIComponent(Claims.split("'").join("''")) + "'";
            // ERROR-SUPPRESSED-JUSTIFIED: a user with no profile / unreadable
            // FirstName is an expected condition. IgnoreError=true keeps it from
            // flipping KErrorMode; on an ignored error the executor invokes the
            // callback with null, and SPUserFirstNameOp.OnResponse then delivers "" (the
            // caller greets by the full display name).
            kSPRestCore.RestExecute("GET", Url, Op.OnResponse, null,
                "Reading FirstName from the user profile", true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore.GetFirstName");
            Op.Deliver("");
        }
    };

    // ── Private methods ──────────────────────────────────────────────────────

    /** Null-tolerant claims stripping. DOCUMENTED EXCEPTION: pure string read, callers own gates and malformed-input faults. */
    public static LoginOf = (Account: string | null | undefined): string => {
        if (Account === null || Account === undefined) { return ""; }
        const Parts: string[] = Account.split("|");
        return Parts[Parts.length - 1];
    };

    /** Null-tolerant membership claim; existing claim prefixes pass through unchanged.
     * DOCUMENTED EXCEPTION: pure string derivation, callers own their gates. */
    public static ClaimsOf = (Account: string | null | undefined): string => {
        const A: string = nz(Account);
        if (!kNotEmptyString(A)) { return ""; }
        if (A.indexOf("|") !== -1) { return A; }
        return "i:0#.f|membership|" + A;
    };

    /** Resolve a claims account for the User Profile service: an account that
     *  already contains "|" is a claims login (used as-is); a bare UPN / e-mail is
     *  wrapped as the standard membership claim. */
    private static _ClaimsAccount = (Account: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            return SPUserCore.ClaimsOf(Account);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore._ClaimsAccount");
            return "";
        }
    };

    private _UserAddedToSiteCollection = (data: any): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.ID = data.d.Id;
            this.ExistsInSiteCollection = true;
            doCallBack(this._EnsureInSiteCollectionCB);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserCore._UserAddedToSiteCollection");
        }
    };
}

// ── SPUsersCore ──────────────────────────────────────────────────────────────

/**
 * One GetFirstName request (2026-10-09 H5): its own callback in a kSettleOnce, so overlapping requests on one SPUserCore never share a slot,
 * and every path (gate, empty account, response, throw) answers the caller exactly once.
 */
export class SPUserFirstNameOp {
    private Once: kSettleOnce<string>;

    public constructor(CallBack: (FirstName: string) => void) {
        this.Once = new kSettleOnce<string>(CallBack);
    }

    /** Answer the caller once. DOCUMENTED EXCEPTION: no error-mode guard - the release path, reached from the error-mode and catch
        branches with "" (F13); a guard would strand the GetFirstName caller. */
    public Deliver = (First: string): void => {
        try {
            this.Once.Settle(First);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserFirstNameOp.Deliver");
        }
    };

    public OnResponse = (Data: any): void => {
        if (getCoreUtils().KErrorMode) { this.Deliver(""); return; }
        try {
            let First: string = "";
            if (kNotNullOrUndefined(Data) && kNotNullOrUndefined(Data.d)) {
                First = nz(Data.d.GetUserProfilePropertyFor);
            }
            this.Deliver(First);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUserFirstNameOp.OnResponse");
            this.Deliver("");
        }
    };
}

export class SPUsersCore extends kObjectArrayCore {
    public Items: SPUserCore[] = [];
    public NewItem: typeof SPUserCore = SPUserCore;

    public GetItemByID = (ID: number): SPUserCore | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            for (const User of this.Items) {
                if (User.ID === ID || User.IDSecond === ID) {
                    return User;
                }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "SPUsersCore.GetItemByID");
            return null;
        }
    };
}
